import {
  chakra,
  Checkbox,
  CheckboxIconProps,
  CheckboxProps,
  PropsOf,
  useMergeRefs,
} from "@chakra-ui/react";
import React from "react";
import { useController } from "react-hook-form";
import { usePrefixedFieldName } from "shared/components/form/FormFieldPrefix";
import { useIsFormDisabled } from "shared/components/form/FormStateProvider";
import { getFieldError } from "shared/utils/FormUtils";

const CheckIcon = (props: PropsOf<typeof chakra.svg>) => (
  <chakra.svg
    width="12px"
    height="12px"
    viewBox="0 0 12 12"
    fill="none"
    {...props}
  >
    <path
      d="M10 3 4.5 8.5 2 6"
      stroke="currentColor"
      strokeWidth={1.667}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </chakra.svg>
);

const IndeterminateIcon = (props: PropsOf<typeof chakra.svg>) => (
  <chakra.svg
    width="12px"
    height="12px"
    viewBox="0 0 12 12"
    fill="none"
    {...props}
  >
    <path
      d="M2.5 6h7"
      stroke="currentColor"
      strokeWidth={1.667}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </chakra.svg>
);

export const CheckboxIcon = ({
  isIndeterminate,
  isChecked,
  ...rest
}: CheckboxIconProps) => {
  void isChecked;
  const BaseIcon = isIndeterminate ? IndeterminateIcon : CheckIcon;
  return isChecked || isIndeterminate ? (
    <chakra.div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: "100%",
      }}
    >
      <BaseIcon {...rest} />
    </chakra.div>
  ) : null;
};

export type FormCheckboxProps = Omit<
  CheckboxProps,
  "name" | "value" | "onChange" | "defaultValue"
> & {
  name: string;
  error?: string;
  defaultValue?: boolean;
  shouldUnregister?: boolean;
  onChangeConfirm?: (checked: boolean) => Promise<boolean>;
};

export const FormCheckbox = React.forwardRef<
  HTMLInputElement,
  FormCheckboxProps
>(
  (
    {
      name,
      defaultValue,
      isDisabled,
      onChangeConfirm,
      shouldUnregister = true,
      ...otherProps
    },
    fwRef,
  ) => {
    const prefixedName = usePrefixedFieldName(name);
    const {
      field: { onChange, ref: fieldRef, ...field },
      formState: { errors },
    } = useController({
      name: prefixedName,
      shouldUnregister,
      defaultValue,
    });
    const mergedRef = useMergeRefs(fieldRef, fwRef);
    const isFormDisabled = useIsFormDisabled();
    return (
      <Checkbox
        {...field}
        {...otherProps}
        ref={mergedRef}
        isChecked={field.value as boolean}
        isDisabled={isDisabled || isFormDisabled}
        isInvalid={!!getFieldError(errors, prefixedName)?.message}
        onChange={(e) => {
          if (!onChangeConfirm) {
            onChange(e);
            return;
          }
          const checked = e.target.checked;
          void (async () => {
            if (await onChangeConfirm(checked)) {
              onChange(checked);
            }
          })();
        }}
      />
    );
  },
);
FormCheckbox.displayName = "FormCheckbox";
